// Festival notification shown in the topbar, just before the version badge.
// Collapsed state is a single pill: today's festival if there is one ("Today
// – <name>"), otherwise the soonest festival within the next 7 festival days. A
// "View More" toggle expands a panel (anchored under the topbar) listing
// every festival in that window, split into a "Today" section and an
// "Upcoming" section so the two are visually distinct; "Show Less" collapses
// back to the pill.
// Dismissal is plain React state (not persisted to storage on purpose) — the
// spec asks for it to reappear on the next refresh or login, i.e. it should
// NOT survive a reload, which a fresh mount's default state already gives us
// for free.
//
// Festival list is the base JSON merged with the same localStorage overlay
// the Admin > Festival page writes to (src/data/festivalOverlay.ts), so
// festivals added there show up here immediately in that browser, without
// waiting for festival-2026.json itself to be updated.
import * as React from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import festivalData from "../data/festival-2026.json";
import { getFestivalImage } from "../data/festivalImages";
import {
    FestivalEntry,
    RawFestivalEntry,
    FESTIVAL_OVERLAY_CHANGED_EVENT,
    loadFestivalOverlay,
    mergeFestivalOverlay,
} from "../data/festivalOverlay";

// "Next 7 days" counts festival occurrences, not calendar days — i.e. the
// next 7 festival entries from today onward, however many calendar days
// they're spread across (gaps between festivals don't count against it).
const WINDOW_SIZE = 7;

const GREETINGS: Record<string, string> = {
    Holiday: "Wishing you a happy holiday!",
    "Tamil Festival": "Wishing you a joyous celebration!",
    Muhurtham: "An auspicious day ahead.",
};

// "Today" for today's date, "Tomorrow" for the next, else a short formatted
// date — used both for the collapsed pill's label and each expanded row.
function relativeDayLabel(dateStr: string, today: moment.Moment): string {
    const diff = moment(dateStr).startOf("day").diff(today, "days");
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    return moment(dateStr).format("ddd, D MMM");
}

export function FestivalNotification() {
    const [dismissed, setDismissed] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [panelPos, setPanelPos] = React.useState<{ top: number; right: number } | null>(null);
    const [overlay, setOverlay] = React.useState(() => loadFestivalOverlay());

    // The topbar (and this component) stays mounted across navigation, so a
    // festival added on the Admin > Festival page in the same tab needs an
    // explicit event — the browser's "storage" event only fires in OTHER
    // tabs. Re-reading on both covers same-tab and cross-tab additions.
    React.useEffect(() => {
        const refresh = () => setOverlay(loadFestivalOverlay());
        window.addEventListener(FESTIVAL_OVERLAY_CHANGED_EVENT, refresh);
        window.addEventListener("storage", refresh);
        return () => {
            window.removeEventListener(FESTIVAL_OVERLAY_CHANGED_EVENT, refresh);
            window.removeEventListener("storage", refresh);
        };
    }, []);

    // Portal'd to document.body (see below) specifically so this floats above
    // EVERYTHING regardless of z-index — the pill lives in the Topbar while
    // page content (e.g. a DataTable's sticky columns) lives in a completely
    // separate scrollable <main>, and those sit in unrelated stacking
    // contexts where a plain z-50 here doesn't reliably win.
    React.useLayoutEffect(() => {
        if (!expanded) return;
        const update = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (rect) setPanelPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [expanded]);

    const { todaysEntries, upcomingEntries, windowEntries } = React.useMemo(() => {
        const today = moment().startOf("day");
        const todayStr = today.format("YYYY-MM-DD");
        const allEntries = mergeFestivalOverlay(festivalData as RawFestivalEntry[], overlay);
        const inWindow = allEntries
            .filter((e) => moment(e.date, "YYYY-MM-DD").isSameOrAfter(today, "day"))
            .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
            .slice(0, WINDOW_SIZE);
        return {
            todaysEntries: inWindow.filter((e) => e.date === todayStr),
            upcomingEntries: inWindow.filter((e) => e.date !== todayStr),
            windowEntries: inWindow,
        };
    }, [overlay]);

    // Default pill: today's first festival if there is one, otherwise the
    // soonest one coming up within the window.
    const primary = todaysEntries[0] || upcomingEntries[0];

    if (dismissed || !primary) return null;

    const today = moment().startOf("day");
    const primaryImage = getFestivalImage(primary.englishName, primary.category);
    const canExpand = windowEntries.length > 1;

    return (
        <div ref={anchorRef} className="relative mz shrink-0">
            <div className="flex items-center gap-1.5 rounded-full border border-violet-300 bg-violet-100/80 py-1 pl-1 pr-1.5 text-violet-900 animate-bounce-soft dark:border-violet-500/40 dark:bg-violet-950/60 dark:text-violet-100">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full">
                    {primaryImage.type === "photo" ? (
                        <img src={primaryImage.url} alt="" className="h-5 w-5 rounded-full object-cover" />
                    ) : (
                        <span className="text-sm leading-none">{primaryImage.value}</span>
                    )}
                </span>
                <span className="text-xs">
                    <span className="font-bold">{relativeDayLabel(primary.date, today)}</span>
                    <span className="mx-1 opacity-60">–</span>
                    <span className="max-w-[140px] truncate font-semibold">{primary.englishName}</span>
                </span>
                {canExpand && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        aria-expanded={expanded}
                        aria-label={expanded ? "Show Less" : "View More festivals"}
                        className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-200 dark:text-violet-200 dark:hover:bg-violet-900"
                    >
                        <span>{expanded ? "Show Less" : "View More"}</span>
                        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    aria-label="Dismiss festival notification"
                    className="shrink-0 rounded-full text-violet-500 transition-colors hover:bg-violet-200 hover:text-violet-800 dark:hover:bg-violet-900 dark:hover:text-violet-100"
                >
                    <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
            </div>

            {expanded && panelPos && createPortal(
                <div
                    className="fixed z-[999] w-80 rounded-xl border bg-popover px-3 py-2 text-popover-foreground shadow-xl"
                    style={{ top: panelPos.top, right: panelPos.right }}
                >
                    <div className="max-h-96 space-y-1.5 overflow-y-auto">
                        {todaysEntries.length > 0 && (
                            <FestivalSection label="Today" entries={todaysEntries} today={today} accent />
                        )}
                        {upcomingEntries.length > 0 && (
                            <FestivalSection label="Upcoming" entries={upcomingEntries} today={today} />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        className="mt-1.5 w-full rounded-lg border py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        Show Less
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
}

function FestivalSection({
    label,
    entries,
    today,
    accent,
}: {
    label: string;
    entries: FestivalEntry[];
    today: moment.Moment;
    accent?: boolean;
}) {
    return (
        <div>
            <p
                className={
                    "mb-1 px-2 text-[11px] font-bold uppercase tracking-wide " +
                    (accent ? "text-violet-600 dark:text-violet-300" : "text-muted-foreground")
                }
            >
                {label}
            </p>
            <div className="space-y-0.5">
                {entries.map((entry) => {
                    const image = getFestivalImage(entry.englishName, entry.category);
                    return (
                        <div key={`${entry.date}-${entry.englishName}`} className="flex items-start gap-2 rounded-lg p-1.5 transition-colors hover:bg-muted">
                            {image.type === "photo" ? (
                                <img src={image.url} alt={entry.englishName} className="h-8 w-8 shrink-0 rounded-lg object-cover" />
                            ) : (
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-base">
                                    {image.value}
                                </span>
                            )}
                            <div className="min-w-0 flex-1 leading-none">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="truncate text-sm font-semibold leading-tight">{entry.tamilName}</p>
                                    <span className="shrink-0 text-[10px] font-medium text-muted-foreground">
                                        {relativeDayLabel(entry.date, today)}
                                    </span>
                                </div>
                                <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{entry.englishName}</p>
                                {entry.tamilDate && <p className="mt-0.5 text-[11px] text-muted-foreground">{entry.tamilDate}</p>}
                                <p className="mt-0.5 text-[11px] text-muted-foreground">{GREETINGS[entry.category]}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default FestivalNotification;
